import { Selectable } from "../models";

/**
 * Converts any entity T into a T & Selectable entity. Use this method
 * where an entity requires an isSelected property which is not defined
 * on the entity.
 * @param item An item onto which an isSelected property will be added.
 * @returns A T & Selectable entity.
 */
export function makeSelectable<T>(item: T): T & Selectable {
  const newItem: T & Selectable = {
    ...item,
    isSelected: false,
  };
  return newItem;
}

/**
 * Converts an array of entities T into an array of T & Selectable entities.
 * Use this method where an array of entities requiring an isSelected property
 * is required but the isSelected property is not defined on the entity.
 * @param arr An array of entities T onto which an isSelected property will be added.
 * @returns An array of T & Selectable entities.
 */
export function makeSelectableArray<T>(arr: T[]): (T & Selectable)[] {
  return arr.map((v) => makeSelectable(v));
}

export const isEmptyValue = (value: unknown) => {
  if (value == null) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length === 0;
  }

  if (typeof value === "number") {
    return Number.isNaN(value) || value === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

export const hasNonEmptyValues = (values: unknown[]) =>
  values.every((value) => !isEmptyValue(value));

export const parseOptionalNumber = <T extends number = number>(
  value: string
) => {
  if (value === "" || value == null) {
    return undefined;
  }

  const parsed = Number(value);

  if (Number.isNaN(parsed) || parsed === 0) {
    return undefined;
  }

  return parsed as T;
};

export const pickNonEmpty = <T extends object, K extends keyof T>(
  source: T,
  keys: readonly K[]
): Partial<Pick<T, K>> => {
  const result: Partial<Pick<T, K>> = {};

  for (const key of keys) {
    const value = source[key];

    if (typeof value === "string") {
      const trimmed = value.trim();

      if (trimmed) {
        result[key] = trimmed as T[K];
      }

      continue;
    }

    if (isEmptyValue(value)) {
      continue;
    }

    result[key] = value;
  }

  return result;
};
